import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  Check,
  ChefHat,
  FileSpreadsheet,
  Layers,
  Pencil,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface DefaultMenuAdmin {
  id: string;
  name: string;
  file_name: string;
  file_size_bytes: number;
  status: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  recipe_count: number;
  recipes_pending: number;
  recipes_processed: number;
  recipes_failed: number;
  // POS2-673: the catalogue this variant is processed against. Null = master.
  catalogue_list_id: string | null;
  archived: boolean;
}

interface CatalogueOption {
  id: string;
  name: string;
  type: string;
  country_code: string | null;
}

const ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const MASTER_LABEL = "Master catalogue";

function catalogueDisplay(c: CatalogueOption): string {
  return c.country_code ? `${c.name} (${c.country_code.toUpperCase()})` : c.name;
}

export default function AdminDefaultMenuPage() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const token = headers.Authorization.replace("Bearer ", "");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const menusQuery = useQuery<{ data: DefaultMenuAdmin[] }>({
    queryKey: ["admin-default-menus", showArchived],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/v1/admin/default-menus?show_archived=${showArchived}`,
        { headers },
      );
      if (!res.ok) throw new Error("Failed to load default menus");
      return res.json();
    },
    enabled: !!token,
    // Poll while any variant is still in Stage-3.
    refetchInterval: (query) =>
      (query.state.data?.data ?? []).some((m) => m.status === "processing") ? 2000 : false,
  });

  const cataloguesQuery = useQuery<CatalogueOption[]>({
    queryKey: ["catalogue-options"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists`, { headers });
      if (!res.ok) return [];
      const body = await res.json();
      return (body.data ?? []).map((l: Record<string, unknown>) => ({
        id: l.id as string,
        name: l.name as string,
        type: l.type as string,
        country_code: (l.country_code as string | null) ?? null,
      }));
    },
    enabled: !!token,
  });

  const catalogues = useMemo(() => cataloguesQuery.data ?? [], [cataloguesQuery.data]);
  const catalogueLabel = useMemo(() => {
    const byId = new Map(catalogues.map((c) => [c.id, c]));
    return (id: string | null) => {
      if (!id) return MASTER_LABEL;
      const c = byId.get(id);
      return c ? catalogueDisplay(c) : "Linked catalogue";
    };
  }, [catalogues]);

  const extractMutation = useMutation({
    mutationFn: async (menuId: string) => {
      const res = await fetch(`${API_URL}/api/v1/admin/default-menus/${menuId}/extract`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Extract failed" }));
        throw new Error(err.detail ?? "Extract failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-default-menus"] });
      toast.success("Recipes extracted — click Process to generate ingredients");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const processMutation = useMutation({
    mutationFn: async (menuId: string) => {
      const res = await fetch(`${API_URL}/api/v1/admin/default-menus/${menuId}/process`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Process failed" }));
        throw new Error(err.detail ?? "Process failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-default-menus"] });
      toast.success("Recipe ingredient generation started — this takes a few minutes");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const renameMutation = useMutation({
    mutationFn: async ({ menuId, name }: { menuId: string; name: string }) => {
      const res = await fetch(`${API_URL}/api/v1/admin/default-menus/${menuId}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Rename failed" }));
        throw new Error(err.detail ?? "Rename failed");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-default-menus"] }),
    onError: (err: Error) => toast.error(err.message),
  });

  const archiveMutation = useMutation({
    mutationFn: async (menuId: string) => {
      const res = await fetch(`${API_URL}/api/v1/admin/default-menus/${menuId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Archive failed" }));
        throw new Error(err.detail ?? "Archive failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-default-menus"] });
      toast.success("Default menu archived");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const unarchiveMutation = useMutation({
    mutationFn: async (menuId: string) => {
      const res = await fetch(`${API_URL}/api/v1/admin/default-menus/${menuId}/unarchive`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Unarchive failed" }));
        throw new Error(err.detail ?? "Unarchive failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-default-menus"] });
      toast.success("Default menu restored");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const menus = menusQuery.data?.data ?? [];

  return (
    <div className="container mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Platform default menus</h1>
          <p className="text-sm text-muted-foreground">
            Fallback menus used when a site hasn't uploaded its own for the event date.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          {menus.length > 0 && (
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Upload default menu
            </Button>
          )}
        </div>
      </div>

      <UploadDefaultMenuDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        catalogues={catalogues}
        headers={headers}
        onUploaded={() => queryClient.invalidateQueries({ queryKey: ["admin-default-menus"] })}
      />

      {menusQuery.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : menus.length > 0 ? (
        <div className="divide-y rounded-lg border bg-card">
          {menus.map((menu) => (
            <VariantRow
              key={menu.id}
              menu={menu}
              catalogueLabel={catalogueLabel(menu.catalogue_list_id)}
              onExtract={() => extractMutation.mutate(menu.id)}
              onProcess={() => processMutation.mutate(menu.id)}
              onRename={(name) => renameMutation.mutate({ menuId: menu.id, name })}
              onArchive={() => archiveMutation.mutate(menu.id)}
              onUnarchive={() => unarchiveMutation.mutate(menu.id)}
              extractPending={extractMutation.isPending && extractMutation.variables === menu.id}
              processPending={processMutation.isPending && processMutation.variables === menu.id}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border bg-card p-10 text-center">
          <FileSpreadsheet className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-medium">No default menus yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            Upload a default menu and link it to a catalogue to give sites a fallback vocabulary.
          </p>
          <Button className="mt-4" onClick={() => setUploadOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Upload default menu
          </Button>
        </div>
      )}
    </div>
  );
}

function VariantRow({
  menu,
  catalogueLabel,
  onExtract,
  onProcess,
  onRename,
  onArchive,
  onUnarchive,
  extractPending,
  processPending,
}: {
  menu: DefaultMenuAdmin;
  catalogueLabel: string;
  onExtract: () => void;
  onProcess: () => void;
  onRename: (name: string) => void;
  onArchive: () => void;
  onUnarchive: () => void;
  extractPending: boolean;
  processPending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(menu.name);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const canExtract =
    !extractPending && ["uploaded", "extracted", "failed", "processed"].includes(menu.status);
  const canProcess =
    !processPending &&
    menu.recipe_count > 0 &&
    ["extracted", "processed", "failed"].includes(menu.status);
  const isProcessing = menu.status === "processing";

  const startEdit = () => {
    setDraft(menu.name);
    setEditing(true);
  };
  const save = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== menu.name) onRename(next);
  };
  const cancel = () => {
    setDraft(menu.name);
    setEditing(false);
  };

  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${menu.archived ? "opacity-60" : ""}`}>
      <ChefHat className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <span className="flex items-center gap-1">
              <input
                ref={inputRef}
                aria-label="Default menu name"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") cancel();
                }}
                className="h-7 w-48 rounded-md border border-input bg-transparent px-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
              />
              <button
                type="button"
                aria-label="Save name"
                onClick={save}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Cancel rename"
                onClick={cancel}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : (
            <>
              <span className="truncate text-sm font-medium text-foreground">{menu.name}</span>
              {!menu.archived && (
                <button
                  type="button"
                  aria-label="Rename"
                  onClick={startEdit}
                  className="rounded p-1 text-muted-foreground/60 hover:bg-muted hover:text-muted-foreground"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
          <Badge variant="outline" className="gap-1">
            <Layers className="h-3 w-3" />
            {catalogueLabel}
          </Badge>
          {menu.archived ? (
            <Badge variant="secondary">Archived</Badge>
          ) : (
            <StatusBadge status={menu.status} />
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {menu.recipe_count > 0
            ? `${menu.recipes_processed}/${menu.recipe_count} recipes`
            : "No recipes yet"}
          {menu.recipes_failed > 0 && (
            <span className="text-destructive"> · {menu.recipes_failed} failed</span>
          )}
          {" · "}
          {menu.file_name}
        </div>
        {isProcessing && menu.recipe_count > 0 && (
          <ProgressBar
            processed={menu.recipes_processed}
            failed={menu.recipes_failed}
            total={menu.recipe_count}
          />
        )}
        {menu.error_message && (
          <p className="mt-1 text-xs text-destructive">{menu.error_message}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {menu.archived ? (
          <Button size="sm" variant="outline" onClick={onUnarchive}>
            <ArchiveRestore className="mr-1.5 h-4 w-4" />
            Unarchive
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" disabled={!canExtract} onClick={onExtract}>
              {extractPending ? "Extracting…" : menu.recipe_count > 0 ? "Re-extract" : "Extract"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!canProcess || isProcessing}
              onClick={onProcess}
            >
              {isProcessing
                ? "Processing…"
                : processPending
                  ? "Starting…"
                  : menu.recipes_processed > 0
                    ? "Re-process"
                    : "Process"}
            </Button>
            <button
              type="button"
              aria-label="Archive"
              onClick={onArchive}
              className="rounded-md p-2 text-muted-foreground/60 hover:bg-muted hover:text-muted-foreground"
            >
              <Archive className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function UploadDefaultMenuDialog({
  open,
  onOpenChange,
  catalogues,
  headers,
  onUploaded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  catalogues: CatalogueOption[];
  headers: Record<string, string>;
  onUploaded: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [catalogueId, setCatalogueId] = useState(""); // "" = master variant
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const countryCatalogues = catalogues.filter((c) => c.type === "country_default");

  const reset = () => {
    setFile(null);
    setCatalogueId("");
    setName("");
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a spreadsheet first");
      const formData = new FormData();
      formData.append("file", file);
      if (catalogueId) formData.append("catalogue_list_id", catalogueId);
      if (name.trim()) formData.append("name", name.trim());
      const uploadHeaders: Record<string, string> = {};
      for (const [k, v] of Object.entries(headers)) {
        if (k !== "Content-Type") uploadHeaders[k] = v;
      }
      const res = await fetch(`${API_URL}/api/v1/admin/default-menu/upload`, {
        method: "POST",
        headers: uploadHeaders,
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Upload failed" }));
        throw new Error(err.detail ?? "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      onUploaded();
      toast.success("Uploaded — click Extract on the variant next");
      reset();
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const pickFile = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    const lower = f.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      toast.error("Please choose an .xlsx or .xls file");
      return;
    }
    setFile(f);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload default menu</DialogTitle>
          <DialogDescription>
            Link this menu to a catalogue. It's processed against that catalogue's items, and
            replaces any existing variant for the same catalogue.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="dm-name">
              Name <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <input
              id="dm-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Platform default menu"
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="dm-catalogue">
              Catalogue
            </label>
            <select
              id="dm-catalogue"
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={catalogueId}
              onChange={(e) => setCatalogueId(e.target.value)}
            >
              <option value="">{MASTER_LABEL} (all regions)</option>
              {countryCatalogues.map((c) => (
                <option key={c.id} value={c.id}>
                  {catalogueDisplay(c)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="dm-file">
              Spreadsheet
            </label>
            <input
              ref={fileInputRef}
              id="dm-file"
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => pickFile(e.target.files)}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-1 flex w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted/40"
            >
              <FileSpreadsheet className="h-4 w-4 shrink-0" />
              <span className="min-w-0 truncate">
                {file ? file.name : "Choose .xlsx spreadsheet"}
              </span>
            </button>
            <p className="mt-1 text-xs text-muted-foreground">
              Columns: # | Menu Item | Meal Period | Category | Description
            </p>
          </div>
          <Button
            className="w-full"
            disabled={!file || uploadMutation.isPending}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProgressBar({
  processed,
  failed,
  total,
}: {
  processed: number;
  failed: number;
  total: number;
}) {
  const processedPct = total > 0 ? (processed / total) * 100 : 0;
  const failedPct = total > 0 ? (failed / total) * 100 : 0;
  return (
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div className="flex h-full">
        <div
          className="bg-emerald-500 transition-all duration-300"
          style={{ width: `${processedPct}%` }}
        />
        <div
          className="bg-destructive transition-all duration-300"
          style={{ width: `${failedPct}%` }}
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<
    string,
    { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
  > = {
    uploaded: { label: "Uploaded", variant: "secondary" },
    extracting: { label: "Extracting…", variant: "secondary" },
    extracted: { label: "Extracted", variant: "secondary" },
    processing: { label: "Processing…", variant: "secondary" },
    processed: { label: "Ready", variant: "default" },
    failed: { label: "Failed", variant: "destructive" },
  };
  const meta = map[status] ?? { label: status, variant: "outline" as const };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}
